import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  Download,
  Loader2,
  ClipboardList,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { CHART_COLORS, ACTIVITY_TYPE_COLORS, CATEGORICAL } from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { toDisplayDate } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'
const COLORS = CHART_COLORS

// GRK-BRAND-4: subtype colours from the activity palette (Task=Navy, Call=Teal,
// other=Slate).
const SUBTYPE_COLORS: Record<string, string> = {
  Task: ACTIVITY_TYPE_COLORS.Task.solid,
  Call: ACTIVITY_TYPE_COLORS.Call.solid,
  '-': CATEGORICAL.slate.solid,
}

type CountRow = {
  owner_name: string | null
  owner_id: string | null
  activity_type: string
  count: number | string
}

/** Per-owner totals by activity type from a summary list. */
function byOwner(rows: CountRow[] | undefined) {
  const out: Record<string, { name: string; total: number; types: Record<string, number> }> = {}
  for (const r of rows ?? []) {
    const key = r.owner_id ?? 'unknown'
    const n = Number(r.count) || 0
    out[key] ??= { name: r.owner_name || 'Unknown', total: 0, types: {} }
    out[key].total += n
    out[key].types[r.activity_type] = (out[key].types[r.activity_type] ?? 0) + n
  }
  return out
}

type PeopleSort = 'name' | 'created' | 'completed'

export default function ActivitiesCreated() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReportFilterValues>(() => getReportDateRange('this_month'))
  const [sortField, setSortField] = useState('created_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [chartMode, setChartMode] = useState<'created' | 'completed'>('created')
  const [peopleSort, setPeopleSort] = useState<PeopleSort>('name')
  const [peopleOrder, setPeopleOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'activities-created', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.startDate) params.set('startDate', filters.startDate)
      if (filters.endDate) params.set('endDate', filters.endDate)
      if (filters.owner_id) params.set('owner_id', filters.owner_id)
      const response = await (apiClient as any).client.get(
        `/reports/activities-created?${params.toString()}`
      )
      return response.data?.data
    },
  })

  // Created and completed per person, for the table and the group headers.
  const created = useMemo(() => byOwner(data?.summary), [data?.summary])
  const completed = useMemo(() => byOwner(data?.completedSummary), [data?.completedSummary])
  const people = useMemo(() => {
    const ids = new Set([...Object.keys(created), ...Object.keys(completed)])
    const rows = [...ids].map((id) => ({
      id,
      name: created[id]?.name ?? completed[id]?.name ?? 'Unknown',
      created: created[id]?.total ?? 0,
      completed: completed[id]?.total ?? 0,
      tasks: completed[id]?.types.Task ?? 0,
      meetings: completed[id]?.types.Event ?? 0,
      calls: completed[id]?.types.Call ?? 0,
      emails: created[id]?.types.Email ?? 0,
    }))
    rows.sort((a, b) => {
      const cmp =
        peopleSort === 'name' ? a.name.localeCompare(b.name) : a[peopleSort] - b[peopleSort]
      return peopleOrder === 'asc' ? cmp : -cmp
    })
    return rows
  }, [created, completed, peopleSort, peopleOrder])

  const sortPeople = (field: PeopleSort) => {
    if (peopleSort === field) setPeopleOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setPeopleSort(field)
      setPeopleOrder(field === 'name' ? 'asc' : 'desc')
    }
  }

  // Build stacked chart data: { name: 'Owner', Task: N, Call: N, Email: N, Event: N }
  const { chartData, subtypes } = useMemo(() => {
    const source: CountRow[] | undefined =
      chartMode === 'created' ? data?.summary : data?.completedSummary
    if (!source) return { chartData: [], subtypes: [] }
    const ownerMap: Record<string, Record<string, string | number>> = {}
    const allSubtypes = new Set<string>()

    for (const row of source) {
      const full = row.owner_name || 'Unknown'
      const name = full.length > 18 ? full.substring(0, 18) + '...' : full
      const entry = (ownerMap[name] ??= { name })
      entry[row.activity_type] = Number(row.count) || 0
      allSubtypes.add(row.activity_type)
    }

    return {
      chartData: Object.values(ownerMap),
      subtypes: Array.from(allSubtypes).sort(),
    }
  }, [data, chartMode])

  // Group details by owner
  const groupedDetails = useMemo(() => {
    if (!data?.details) return []
    const sorted = [...data.details].sort((a: any, b: any) => {
      const aVal = (a[sortField] ?? '').toString().toLowerCase()
      const bVal = (b[sortField] ?? '').toString().toLowerCase()
      const cmp = aVal.localeCompare(bVal)
      return sortOrder === 'asc' ? cmp : -cmp
    })

    // Build owner count lookup from ownerTotals
    const ownerCounts: Record<string, number> = {}
    if (data.ownerTotals) {
      for (const ot of data.ownerTotals) {
        ownerCounts[ot.owner_id] = parseInt(ot.total_count)
      }
    }

    const groups: { owner_name: string; owner_id: string; count: number; rows: any[] }[] = []
    let currentGroup: (typeof groups)[0] | null = null

    for (const row of sorted) {
      if (!currentGroup || currentGroup.owner_id !== row.owner_id) {
        currentGroup = {
          owner_name: row.owner_name || 'Unknown',
          owner_id: row.owner_id,
          count: ownerCounts[row.owner_id] || 0,
          rows: [],
        }
        groups.push(currentGroup)
      }
      currentGroup.rows.push(row)
    }
    return groups
  }, [data?.details, data?.ownerTotals, sortField, sortOrder])

  const handleExport = () => {
    if (data?.details) {
      exportToCSV({
        filename: 'Activities_Created',
        data: data.details,
        columns: [
          'owner_name',
          'activity_type',
          'created_date',
          'account_name',
          'opportunity_name',
          'contact_name',
          'lead_name',
          'subject',
        ],
        headers: [
          'Created By',
          'Activity Type',
          'Date',
          'Company / Account',
          'Opportunity',
          'Contact',
          'Lead',
          'Subject',
        ],
      })
    }
  }

  const columns = [
    { field: 'activity_type', label: 'Activity Type' },
    { field: 'created_date', label: 'Date' },
    { field: 'account_name', label: 'Company / Account' },
    { field: 'opportunity_name', label: 'Opportunity' },
    { field: 'contact_name', label: 'Contact' },
    { field: 'lead_name', label: 'Lead' },
    { field: 'subject', label: 'Subject' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#0E7490]" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Activity Created - This Month</h1>
              <Badge variant="secondary">Pre-built</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              {data
                ? `${(data.totalRecords ?? 0).toLocaleString()} created · ${(data.totalCompleted ?? 0).toLocaleString()} completed`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="this_month"
            datePresets={['today', 'this_month', 'last_month', 'last_90_days', 'all', 'custom']}
          />
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!data?.details?.length}
            aria-label="Export to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="ml-2">Loading report...</span>
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{explainError(error, "Couldn't load the report")}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && data && (
        <>
          {/* Created vs completed per person */}
          <Card>
            <CardHeader>
              <CardTitle>Created and completed by team member</CardTitle>
              <CardDescription>
                Completed counts tasks marked done, meetings that took place and calls logged in the
                period, whenever they were created. Emails count as created only. Email counts
                include every team member, but only the number is shared: nobody can read anyone
                else's emails.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {people.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {(
                          [
                            ['name', 'Team member'],
                            ['created', 'Created'],
                            ['completed', 'Completed'],
                          ] as [PeopleSort, string][]
                        ).map(([field, label]) => (
                          <TableHead
                            key={field}
                            className={field === 'name' ? undefined : 'text-right'}
                            aria-sort={
                              peopleSort === field
                                ? peopleOrder === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : undefined
                            }
                          >
                            <button
                              className="inline-flex items-center gap-1 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded"
                              onClick={() => sortPeople(field)}
                              aria-label={`Sort by ${label}`}
                            >
                              {label}
                              {peopleSort === field ? (
                                peopleOrder === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                                ) : (
                                  <ArrowDown className="h-3 w-3" aria-hidden="true" />
                                )
                              ) : (
                                <ArrowUpDown className="h-3 w-3 opacity-30" aria-hidden="true" />
                              )}
                            </button>
                          </TableHead>
                        ))}
                        <TableHead className="text-right">Tasks done</TableHead>
                        <TableHead className="text-right">Meetings held</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Emails</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {people.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.created.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.completed.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.tasks.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.meetings.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.calls.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.emails.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No data for selected period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Stacked Bar Chart by activity type */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle>
                  {chartMode === 'created' ? 'Activities created' : 'Activities completed'} by team
                  member
                </CardTitle>
                <CardDescription>
                  {chartMode === 'created'
                    ? 'Tasks, meetings, emails and calls created per team member'
                    : 'Tasks done, meetings held and calls logged per team member'}
                </CardDescription>
              </div>
              <div role="group" aria-label="Chart shows" className="flex gap-1">
                <Button
                  size="sm"
                  variant={chartMode === 'created' ? 'default' : 'outline'}
                  aria-pressed={chartMode === 'created'}
                  onClick={() => setChartMode('created')}
                >
                  Created
                </Button>
                <Button
                  size="sm"
                  variant={chartMode === 'completed' ? 'default' : 'outline'}
                  aria-pressed={chartMode === 'completed'}
                  onClick={() => setChartMode('completed')}
                >
                  Completed
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend />
                    {subtypes.map((subtype) => (
                      <Bar
                        key={subtype}
                        dataKey={subtype}
                        stackId="a"
                        fill={SUBTYPE_COLORS[subtype] || COLORS[4] || CATEGORICAL.slate.solid}
                        name={subtype === '-' ? '(none)' : subtype}
                        radius={
                          subtypes.indexOf(subtype) === subtypes.length - 1
                            ? [0, 4, 4, 0]
                            : undefined
                        }
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No data for selected period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Detail Table grouped by owner */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Details</CardTitle>
              <CardDescription>
                {data.details?.length || 0} records
                {data.details?.length >= 500 ? ' (showing first 500)' : ''}. Emails listed here are
                your own; the counts above include everyone{"'"}s.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((col) => (
                        <TableHead key={col.field}>
                          <button
                            className="flex items-center gap-1 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded"
                            onClick={() => handleSort(col.field)}
                            aria-label={`Sort by ${col.label}`}
                          >
                            {col.label}
                            {sortField === col.field ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp className="h-3 w-3" aria-hidden="true" />
                              ) : (
                                <ArrowDown className="h-3 w-3" aria-hidden="true" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-30" aria-hidden="true" />
                            )}
                          </button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedDetails.map((group) => (
                      <>
                        {/* Owner group header */}
                        <TableRow key={`header-${group.owner_id}`} className="bg-muted/50">
                          <TableCell colSpan={7} className="font-semibold py-2">
                            {group.owner_name} ({group.count.toLocaleString()} created ·{' '}
                            {(completed[group.owner_id ?? 'unknown']?.total ?? 0).toLocaleString()}{' '}
                            completed)
                          </TableCell>
                        </TableRow>
                        {/* Detail rows */}
                        {group.rows.map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Badge variant="secondary">{row.activity_type || '-'}</Badge>
                            </TableCell>
                            <TableCell>
                              {row.created_date
                                ? (
                                    toDisplayDate(row.created_date) ?? new Date(row.created_date)
                                  ).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {row.account_name && row.what_id ? (
                                <Link
                                  to={`/accounts/${row.what_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {row.account_name}
                                </Link>
                              ) : (
                                row.account_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {row.opportunity_name && row.what_id ? (
                                <Link
                                  to={`/opportunities/${row.what_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {row.opportunity_name}
                                </Link>
                              ) : (
                                row.opportunity_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {row.contact_name && row.who_id ? (
                                <Link
                                  to={`/contacts/${row.who_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {row.contact_name}
                                </Link>
                              ) : (
                                row.contact_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {row.lead_name && row.who_id ? (
                                <Link
                                  to={`/leads/${row.who_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {row.lead_name}
                                </Link>
                              ) : (
                                row.lead_name || '-'
                              )}
                            </TableCell>
                            <TableCell className="max-w-[300px] truncate">
                              {row.subject || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
